<template>
  <div>
    <div v-if="!show404 && !show403" class="sx-page pp-v2">
      <!-- On its process's page (US 9369 + 9371): the process's hero and row, then the activity's own header -->
      <template v-if="$route.params.process">
        <ProcessHero v-if="process.id"
                     :process="process"
                     :row="switcherRow"
                     :link-name="true"
                     @edit="$refs.processInfo.openEdit()"
                     @delete="$refs.processInfo.openDelete()"
                     @history="openProcessHistory"></ProcessHero>
        <V2PageLoading v-else part="hero"></V2PageLoading>
        <ProcessSwitcher ref="switcher" :activity="$route.params.slug" @loaded="switcherRow = $event"></ProcessSwitcher>
        <!-- The process's edit and delete dialogs, for the hero's buttons -->
        <ProcessBasicInfoBlock v-if="process.id" ref="processInfo" :object="process" @updated="loadProcess" hideHeader></ProcessBasicInfoBlock>
      </template>
      <!-- An activity without a process keeps its own page -->
      <b-breadcrumb v-else :items="breadcrumbItems"></b-breadcrumb>

      <!-- The activity's edit and delete dialogs, and an open update task; its header shows the rest -->
      <BasicInfoBlock v-if="activity.id" ref="basicInfo" :object="activity" @updated="updateActivity" :edit="edit" hideHeader></BasicInfoBlock>

      <!-- Our own tab bar, as on the system and contract pages (US 9370, 9371). History opens from the activity's header. -->
      <nav v-if="activity.id" class="sx-tabs mt-3" role="tablist" qid="activity-tabs">
        <button v-for="item in tabItems"
                :key="item.key"
                type="button"
                role="tab"
                class="sx-tabs__tab"
                :class="{ on: tab === item.key }"
                :aria-selected="tab === item.key ? 'true' : 'false'"
                :qid="'activity-tab-' + item.key"
                @click="openTab(item.key)">
          <font-awesome-icon :icon="item.icon"/>{{ item.label }}
        </button>
      </nav>
      <V2PageLoading v-else part="tabs"></V2PageLoading>

      <div v-if="activity.id" class="sx-tabs__content">
        <!-- The picked activity's own header: Edit, ⋯ and History are the activity's, the hero's are the process's -->
        <ActivityHead class="mt-3"
                      :activity="activity"
                      :index="activityIndex"
                      :count="activityCount"
                      :has-process="!!activity.process_id"
                      :history-on="tab === 'history'"
                      @edit="$refs.basicInfo.openEdit()"
                      @move="$refs.move.show()"
                      @delete="$refs.basicInfo.openDelete()"
                      @history="openTab(tab === 'history' ? 'basic-info' : 'history')"></ActivityHead>

        <!-- Basic info -->
        <div v-show="tab === 'basic-info'" role="tabpanel" class="pp-stack" qid="activity-tab-basic-info">
          <DescriptionBlock :object="activity" @updated="updateActivity"></DescriptionBlock>
          <OrganizationsTable :object="activity" @updated="updateActivity"></OrganizationsTable>
          <PoliciesAndProceduresTable :object="activity" @updated="updateActivity" v-if="getClient.policyModule"></PoliciesAndProceduresTable>
        </div>

        <!-- Data mapping, loaded the first time it's opened -->
        <div v-if="opened['data-flow']" v-show="tab === 'data-flow'" role="tabpanel" class="pp-stack data-mapping-tab" qid="activity-tab-data-mapping">
          <MappingDescriptionBlock :object="activity" @updated="updateActivity"></MappingDescriptionBlock>
          <SystemTable :object="activity" v-if="getClient.systemModule" @updated="updateActivity"></SystemTable>
          <ThirdPartySourcesTable :object="activity" v-if="getClient.contractBasicModule && getClient.gdprAdvancedModule" @updated="updateActivity"></ThirdPartySourcesTable>
          <ThirdPartyReceiversTable :object="activity" v-if="getClient.contractBasicModule && getClient.gdprModule" @updated="updateActivity"></ThirdPartyReceiversTable>
        </div>

        <!-- GDPR, loaded the first time it's opened -->
        <div v-if="opened.gdpr && getClient.gdprModule && activity.privacy_feature" v-show="tab === 'gdpr'" role="tabpanel" class="pp-stack gdpr-tab" qid="activity-tab-gdpr">
          <ProcessingRole :object="activity" @updated="updateActivity"></ProcessingRole>
          <DataSubjectsTable :object="activity" @updated="updateActivity"></DataSubjectsTable>
          <RetentionRulesTable :object="activity" @updated="updateActivity"></RetentionRulesTable>
          <IntegrityCheck :object="activity" @updated="updateActivity"></IntegrityCheck>
          <MeasuresTable :object="activity" @updated="updateActivity"></MeasuresTable>
          <ProcessingType :object="activity" @updated="updateActivity" v-if="getClient.gdprAdvancedModule"></ProcessingType>
        </div>

        <!-- Risk scenarios: on its process's page, the activity, its process and the other activities in one tab -->
        <div v-if="tab === 'risk-management' && getClient.riskModule" role="tabpanel" qid="activity-tab-risk-management">
          <AssetRiskTab asset="activities" catalogue-type="activity" :object="activity"
              :can-create="isGranted([permissions.ACTIVITY_RISK_SCENARIO_CREATE, permissions.ACTIVITY_RISK_SCENARIO_CREATE_OWN], activity) && (!getClient.parent || activity.partition)"
              :can-edit="isGranted([permissions.ACTIVITY_RISK_SCENARIO_EDIT, permissions.ACTIVITY_RISK_SCENARIO_EDIT_OWN], activity) && (!getClient.parent || activity.partition)"
              :can-delete="isGranted([permissions.ACTIVITY_RISK_SCENARIO_DELETE, permissions.ACTIVITY_RISK_SCENARIO_DELETE_OWN], activity) && (!getClient.parent || activity.partition)"
              :sources="riskSources"
              :picked-key="riskSources ? 'activity-' + activity.id : null"
              @updated="updateActivity"></AssetRiskTab>
        </div>

        <!-- Assessments: on its process's page, the process's and its activities' in one tab (US 9369 + 9371) -->
        <div v-if="tab === 'assessments' && getClient.assessmentModule && activity.assessment_feature" role="tabpanel" qid="activity-tab-assessments">
          <ProcessAssessmentsTab v-if="$route.params.process && process.id" :process="process" :picked-id="activity.id"
              :can-start="!!getClient.projectModule && isGranted(permissions.PROJECT_CREATE)"
              @start="$refs.createProject.showGoal('risk')"></ProcessAssessmentsTab>
          <AssessmentsTable v-else :activity="activity" @updated="updateActivity"></AssessmentsTable>
        </div>

        <!-- History, from the activity's header -->
        <div v-if="tab === 'history'" role="tabpanel" qid="activity-tab-history">
          <ActivityHistory :object="activity"></ActivityHistory>
        </div>
      </div>
      <template v-else>
        <V2PageLoading class="mt-3" part="head"></V2PageLoading>
        <V2PageLoading part="cards"></V2PageLoading>
      </template>

      <!-- An activity belongs to one process: move it to another (US 9369 + 9371) -->
      <ActivityMove v-if="activity.id" ref="move" :activity="activity" :process="process.id ? process : null" @moved="moved"></ActivityMove>
      <!-- Opens the project launcher on a risk assessment (US 9369 + 9371) -->
      <span class="pm-hidden-trigger">
        <CreateProjectModal v-if="getClient.projectModule && activity.id" ref="createProject" type="risk_assessment" :types="['risk_assessment']"></CreateProjectModal>
      </span>
    </div>

    <div v-if="show404">
      <page404></page404>
    </div>

    <div v-if="show403">
      <page403></page403>
    </div>

  </div>
</template>

<script>
import {initialsAvatarUrl} from '@/util/userAvatar.js';
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import ActivityEditForm from '@/views/Activities/ActivityEditForm.vue';
import Avatar from '@/components/Avatar.vue';
import DataSubjectsTable from '@/views/Activities/DataSubjectsTable.vue';
import DescriptionBlock from '@/views/Activities/DescriptionBlock.vue';
import MappingDescriptionBlock from '@/views/Activities/MappingDescriptionBlock.vue';
import PoliciesAndProceduresTable from '@/views/Activities/PoliciesAndProceduresTable.vue';
import BasicInfoBlock from '@/views/Activities/BasicInfoBlock.vue';
import ProcessSwitcher from '@/components/processes/ProcessSwitcher.vue';
import ProcessHero from '@/components/processes/ProcessHero.vue';
import {processRiskSources} from '@/components/processes/riskSources.js';
import ProcessAssessmentsTab from '@/components/processes/ProcessAssessmentsTab.vue';
import CreateProjectModal from '@/views/Projects/CreateProjectModal.vue';
import OrganizationsTable from '@/views/Activities/OrganizationsTable.vue';
import RetentionRulesTable from '@/views/Activities/RetentionRulesTable.vue';
import SystemTable from '@/views/Activities/SystemTable.vue';
import ProcessBasicInfoBlock from '@/views/Processes/BasicInfoBlock.vue';
import ActivityHead from '@/components/processes/ActivityHead.vue';
import ActivityMove from '@/components/processes/ActivityMove.vue';
import V2PageLoading from '@/components/V2PageLoading.vue';
import {v2ModalsOn, v2ModalsOff} from '@/util/v2Modals.js';
import ThirdPartySourcesTable from '@/views/Activities/ThirdPartySourcesTable.vue';
import ThirdPartyReceiversTable from '@/views/Activities/ThirdPartyReceiversTable.vue';
import DataObjectCreateForm from '@/views/DataObjects/DataObjectCreateForm.vue';
import IntegrityCheck from '@/views/Activities/IntegrityCheck.vue';
import MeasuresTable from '@/views/Activities/MeasuresTable.vue';
import ProcessingType from '@/views/Activities/ProcessingType.vue';
import ProcessingRole from '@/views/Activities/ProcessingRole.vue';
import AssetRiskTab from '@/components/risk/AssetRiskTab.vue';
import ActivityHistory from '@/views/Activities/ActivityHistory.vue';
import ValidationList from '@/views/Validations/ValidationList.vue';
import ValidationForm from '@/views/Validations/ValidationForm.vue';
import ShowMoreModal from '@/components/ShowMoreModal.vue';
import page404 from '@/components/404block.vue';
import page403 from '@/components/403block.vue';
import AssessmentsTable from '@/views/Activities/AssessmentsTable.vue';

export default {
  name: 'ActivityView',
  data: function() {
    return {
      process: {},
      switcherRow: null,
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        },
        {
          text: this.$t('processes.title'),
          to: '/processing-activities'
        }
      ],
      descriptionLength: config.descriptionLength,
      showFullDescription: false,
      activity: {},
      edit: this.$route.params.edit,
      selectedSort: '',
      selectedDirection: '',
      lbTableItems: [],
      lbLoading: true,

      itemToDelete: {},
      tags: [],
      users: [],
      error: '',
      lbForm: {
        name: '',
        article_reason: '',
        links: []
      },
      inputLink: '',
      selectedFiles: [],
      showDescriptionInput: false,
      showMappingDescriptionInput: false,
      showPurposeInput: false,
      activityEditDescription: '',
      activityEditMappingDescription: '',

      // The open tab, and the tabs opened once (they stay loaded)
      tab: 'basic-info',
      opened: { 'basic-info': true },

      buttonDisabled: false,
      showTaskToolbar: false,
      activeTask: 0,
      tabIndex: 0,
      myTasks: [],
      myGuides: [],
      validationShow: false,
      updateValidationList: false,
      validation: {},
      validationOptions: [],
      selectedItem: {},
      showTagsModal: false,
      show404: false,
      show403: false,
    }
  },
  computed: {
    // On its process's page: the activity, its process and the other activities in one tab (US 9369 + 9371)
    riskSources() {
      if (!this.$route.params.process || !this.process.id || !this.switcherRow || !this.activity.id) {
        return null;
      }
      return processRiskSources(this, this.process, this.switcherRow, this.activity);
    },
    tabItems() {
      let client = this.getClient;
      return [
        { key: 'basic-info', icon: 'th-large', label: this.$t('activities.tab.basic_info') },
        { key: 'data-flow', icon: 'project-diagram', label: this.$t('activities.tab.data_mapping') },
        { key: 'gdpr', icon: 'user-shield', label: this.$t('activities.tab.gdpr'), module: !!(client.gdprModule && this.activity.privacy_feature) },
        { key: 'risk-management', icon: 'exclamation-triangle', label: this.$t('activities.tab.risk_management'), module: !!client.riskModule },
        { key: 'assessments', icon: 'chart-line', label: this.$t('systems.tab.assessments'), module: !!(client.assessmentModule && this.activity.assessment_feature) }
      ].filter(item => item.module === undefined || item.module);
    },
    // Its place among its process's activities, for its header
    ownActivities() {
      return this.switcherRow ? this.switcherRow.children.filter(child => !child.shared) : [];
    },
    activityIndex() {
      return Math.max(this.ownActivities.map(child => child.id).indexOf(this.activity.id) + 1, 1);
    },
    activityCount() {
      return this.ownActivities.length || 1;
    },
    getClient() {
      return this.$store.state.client
    },
    getTabIndex() {
      return this.$store.state.tabIndex
    },
    getFiles() {
      return this.selectedFiles
    },
    getReloadContent() {
      return this.$store.state.updateContent
    },
    getReloadPage() {
      return this.$store.state.updatePage
    }
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug && oldValue.slug && newValue.slug !== oldValue.slug) {
        let self = this;
        setTimeout(function() {
          if (self.$route.name == 'activities.view' || self.$route.name == 'processes.activity') {
            self.goToList()
          }
        }, 800);
      } else {
        this.loadContent(true);
      }
    },
    getReloadContent: function () {
      if (!this.showOnTask) {
        if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
          if (this.$store.state.updatedObject.id == this.activity.id && this.$store.state.updatedObject.who_am_i == this.activity.who_am_i) {
            this.activity = this.$store.state.updatedObject
          }
        }
      }
    },
    getReloadPage: function () {
      if (this.$store.state.updatedObject && this.$store.state.updatedObject.id && this.$store.state.updatedObject.id == this.activity.id && this.$store.state.updatedObject.who_am_i == this.activity.who_am_i) {
        this.$router.push({ name: 'activities.view', params: { slug: this.$store.state.updatedObject.slug }, query: { task: this.$route.query.task }})
      } else {
        this.$router.push({ name: 'activities.view', params: { slug: this.activity.slug }, query: { task: this.$route.query.task }})
      }
    },
    getTabIndex: function (newValue, oldValue) {
      this.handleUpdateTab(newValue)
    }
  },
  components: {
    ProcessSwitcher,
    ProcessHero,
    ProcessAssessmentsTab,
    CreateProjectModal,
    ActivityEditForm,
    Avatar,
    DataSubjectsTable,
    DescriptionBlock,
    MappingDescriptionBlock,
    PoliciesAndProceduresTable,
    OrganizationsTable,
    RetentionRulesTable,
    DataObjectCreateForm,
    IntegrityCheck,
    MeasuresTable,
    ProcessingType,
    ProcessingRole,
    SystemTable,
    ProcessBasicInfoBlock,
    ActivityHead,
    ActivityMove,
    V2PageLoading,
    ThirdPartyReceiversTable,
    ThirdPartySourcesTable,
    AssetRiskTab,
    ActivityHistory,
    ValidationList,
    ValidationForm,
    ShowMoreModal,
    AssessmentsTable,
    BasicInfoBlock,
    page403,
    page404
  },
  methods: {
    // History opens from the activity's header, the other tabs from our tab bar
    openTab(key) {
      key = { risk_management: 'risk-management', 'data-mapping': 'data-flow', 'eu-gdpr': 'gdpr' }[key] || key;
      if (key !== 'history' && !this.tabItems.some(item => item.key === key)) {
        return;
      }
      this.tab = key;
      this.$set(this.opened, key, true);
    },
    // The hero's History button: the process's history, on the process itself
    openProcessHistory() {
      this.$router.push({ name: 'processes.view', params: { slug: this.process.slug }, query: { tab: 'history' } });
    },
    // Moved to another process: open it there
    moved(target) {
      this.$router.push({ name: 'processes.activity', params: { process: target.slug, slug: this.activity.slug } });
    },
    setValidation(object) {
      this.validation = object
    },
    handleValidationModalClose() {
      this.updateValidationList = true
      this.loadContent()
    },
    openTagsModal(object) {
      this.selectedItem = object
      this.showTagsModal = true
    },
    closeTagsModal() {
      this.selectedItem = {}
      this.showTagsModal = false
    },
    getReducedTags(tags) {
      let reducedTags = []
      if (tags.length > 3) {
        reducedTags = _.cloneDeep(tags);
        reducedTags = reducedTags.slice(0, 3);
      } else {
        reducedTags = tags;
      }
      return reducedTags;
    },
    goToList() {
      this.$router.push('/processing-activities')
    },
    getTask(code) {
      let existingTask = _.find(this.myTasks, function(object) {
        return object.code == code
      });
      return existingTask
    },
    loadTasks(code) {
      let self = this;
      if (this.getClient.projectModule) {
        piincHttp.get('projects/me/tasks', { params:
          {
            per_page: 1,
            status: 'pending',
            model_type: this.activity.who_am_i,
            model_id: this.activity.id,
            code: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myTasks.push(response.data.items[0])
          }
        }, function() {});
      }
    },
    openToolbar(object) {
      this.$store.commit('storeTaskToolbarState', true);
      this.$store.commit('storeActiveTask', object);
    },
    openHelpCenter(object) {
      let topElement = document.getElementById("app");
      if (topElement.classList.contains("guide-collapsed")) {
        topElement.classList.toggle("guide-collapsed");
      }
      if (!topElement.classList.contains("activity-collapsed")) {
        topElement.classList.toggle("activity-collapsed");
      }

      this.$store.commit('storeHelpCenterState', true);
      this.$store.commit('storeActiveGuideScope', object);
    },
    // Tasks and guides ask for a tab by name
    handleUpdateTab(response) {
      this.openTab(response);
    },
    updateActivity() {
      this.loadContent();
    },
    downloadMedia(object){
      piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = object.file_name;

        link.click();
        link.remove()
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    handleLink() {
      // If there's a link in input, add it to links array
      if (this.inputLink !== '') {
        if (!this.inputLink.startsWith('http')) {
          this.inputLink = 'http://' + this.inputLink
        }
        this.lbForm.links.push(this.inputLink)
        // Reset link input form
        this.inputLink = ''
      }
    },
    removeLinkItem(index) {
      // Remove link item from the links array
      this.lbForm.links.splice(index, 1);
    },
    reloadContent(object) {
      // If slug is not changed, just reload content
      if (object.slug == this.activity.slug) {
        // Remove last item in breadcrumbs, because it will be added again on load content
        this.breadcrumbItems.pop();
        this.loadContent(true);
        this.$emit('updated', object);
      } else {
        this.$store.commit('storeUpdateTasks', true);
        // If slug changed, reload state with new param
        this.$router.push({ name: 'activities.view', params: { slug: object.slug }})
      }
    },
    getUserAvatar(user){
      if (user && user.avatar) {
        return user.avatar;
      } else {
        return initialsAvatarUrl(user);
      }
    },
    getGuide(code) {
      let existingGuide = _.find(this.myGuides, function(object) {
        return object.scope == code && object.type == 'Activity' && object.enabled
      });
      return existingGuide
    },
    // The process whose hero the activity is shown under
    loadProcess() {
      if (!this.$route.params.process) {
        return;
      }
      let self = this;
      piincHttp.get('processes/' + this.$route.params.process, { params: { client: this.getClient.slug } }).then(function(response) {
        self.process = response.data;
      }, function() {
        self.process = {};
      });
    },
    loadContent(breadcrumbPush) {
      this.loadProcess();
      if (this.$refs.switcher) {
        this.$refs.switcher.reload();
      }
      let self = this;
      piincHttp.get('activities/' + this.$route.params.slug, { params:
        {
          client: this.getClient.slug
        }
      }).then(function(response) {
        self.activity = response.data;
        self.validationOptions = self.activity.proportionalityValidationOptions;
        self.loadTasks('update_basic_info');
        if (breadcrumbPush) {
          // Push Third party name in breadcrumb array
          self.breadcrumbItems.push({text: self.activity.name, active: true});
        }
        if (self.$route.query.tab) {
          self.openTab(self.$route.query.tab);
        }
      }, function(error) {
        if (error.status == 404) {
          self.show404 = true
        }
        if (error.status == 403) {
          self.show403 = true
        }
      });
    },
    loadGuides(code) {
      let self = this;
      let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

      piincHttp.get('guides', { params:
        {
          per_page: 1,
          language: 'en',
          type: 'Activity',
          scope: code,
          client: this.getClient.slug
        }
      }).then(function(response) {
        if (response.data.items.length) {
          self.myGuides.push(response.data.items[0])
        }
      }, function() {});
    }
  },
  // Dialogs of the old sections get the new look while this page is open
  mounted() {
    v2ModalsOn();
  },
  beforeDestroy() {
    v2ModalsOff();
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('storeTabIndex', '');
    next()
  },
  created () {
    let self = this;
    if (this.getClient && this.getClient.slug) {
      this.loadContent(true);
    }

    if (this.$route.params.tabIndex && this.$route.params.validation) {
      // A validation link: 1 = data mapping, 2 = GDPR, 3 = risk management
      setTimeout(function(){
        self.openTab({ 1: 'data-flow', 2: 'gdpr', 3: 'risk-management' }[self.$route.params.tabIndex] || 'basic-info');
      }, 500);
      setTimeout(function(){
        var element = document.getElementsByClassName(self.$route.params.validation);
        element[0].scrollIntoView(true);
        window.scrollBy(0, -100);
        element[0].classList.add("highlight-validation");
      }, 2000);
    }

    this.loadGuides('update_basic_info');
  }
}
</script>
